import React from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api, resolveApiAssetUrl } from '../lib/api'
import { asCollection } from '../lib/collections'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import CategoryLandingEditorModeToggle from '../components/categoryLanding/CategoryLandingEditorModeToggle'
import CategoryLandingJsonEditorCard from '../components/categoryLanding/CategoryLandingJsonEditorCard'
import CategoryLandingLivePreview from '../components/categoryLanding/CategoryLandingLivePreview'
import CategoryLandingSectionBlockEditor from '../components/categoryLanding/CategoryLandingSectionBlockEditor'
import CategoryLandingLayoutColorField, { isCategoryLandingLayoutColorKey } from '../components/categoryLanding/CategoryLandingLayoutColorField'
import CategoryLandingFieldListEditor from '../components/categoryLanding/CategoryLandingFieldListEditor'
import useCategoryLandingEditorState, { prettyJsonValue } from '../hooks/useCategoryLandingEditorState'
import MenuGuideButton from '../components/MenuGuideButton'
import {
  CATEGORY_OPTIONS as CATEGORIES,
  applyCategoryLandingLayout,
  createCategoryLandingDefaultFormFields,
  getCategoryLandingLayoutEditableFields,
  getCategoryLandingLayoutOptions,
  getCodeTemplateApplyOptions,
  getCodeTemplatePresetHint,
  getCodeTemplatePreset,
  isLeadGenFullbleedLayoutKey,
  normalizeCategoryLandingLayoutConfig,
} from '../templates/categoryLanding/registry'

function TemplateThumb({ src, alt, className = '' }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 ${className}`}>
        미리보기 없음
      </div>
    )
  }
  return <img src={resolveApiAssetUrl(src)} alt={alt} className={`rounded-2xl object-cover ${className}`} />
}

function getEmptySurveyField() {
  return { id: '', type: 'text', label: '', required: false, placeholder: '' }
}

function getEmptyTemplateForm() {
  return {
    name: '',
    heroTitle: '',
    heroSubtitle: '',
    heroImageFileId: '',
    formTitle: '',
    contentSections: '[]',
    formFields: prettyJsonValue(createCategoryLandingDefaultFormFields()),
    surveyCustomFields: '[]',
    customConfig: '{}',
  }
}

function buildTemplatePayload(form, category) {
  return {
    category,
    name: form.name,
    heroTitle: form.heroTitle,
    heroSubtitle: form.heroSubtitle,
    heroImageFileId: form.heroImageFileId,
    formTitle: form.formTitle,
    contentSections: form.contentSections,
    formFields: form.formFields,
    surveyCustomFields: form.surveyCustomFields,
    customConfig: form.customConfig,
  }
}

function coerceLayoutFieldValue(field, value) {
  if (field?.type === 'number') return Number(value || 0)
  return value
}

function getBasicTemplateLoadMessage(style, fromCode = false) {
  const prefix = fromCode ? '코드 템플릿에서 ' : ''
  const templateNo = style === 'clean-form'
    ? '2'
    : style === 'hanwha-sections'
      ? '3'
      : style === 'basic-template-4'
        ? '4'
        : style === 'basic-template-5'
          ? '5'
          : isLeadGenFullbleedLayoutKey(style)
            ? '6'
            : '1'
  return `${prefix}기본 템플릿 ${templateNo}를 불러왔습니다`
}

function JsonErrorNotice({ error, label }) {
  if (!error) return null
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      `{label}` JSON 형식이 올바르지 않습니다: {error}
    </div>
  )
}

export default function CategoryLandingTemplatesPage() {
  const [selected, setSelected] = React.useState(CATEGORIES[0].code)
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(null)
  const [selectedCodeTemplateKey, setSelectedCodeTemplateKey] = React.useState('default')
  const [form, setForm] = React.useState(getEmptyTemplateForm())
  const [editorMode, setEditorMode] = React.useState('block')
  const [uploadingTarget, setUploadingTarget] = React.useState('')
  const codeTemplateOptions = React.useMemo(() => getCodeTemplateApplyOptions(selected), [selected])
  const codeTemplateCards = React.useMemo(
    () => codeTemplateOptions
      .map((option) => ({
        ...option,
        preset: getCodeTemplatePreset(selected, option.presetKey),
      }))
      .filter((item) => !!item.preset),
    [codeTemplateOptions, selected],
  )
  const selectedCodeTemplateHint = React.useMemo(
    () => getCodeTemplatePresetHint(selectedCodeTemplateKey),
    [selectedCodeTemplateKey],
  )
  const layoutOptions = React.useMemo(() => getCategoryLandingLayoutOptions(selected), [selected])

  const listQuery = useQuery({
    queryKey: ['category-landing-template-list', selected],
    queryFn: async () => {
      const res = await api.get('/category-landing-templates', { params: { category: selected }, __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    refetchOnWindowFocus: false,
  })

  const detailQuery = useQuery({
    queryKey: ['category-landing-template-detail', selectedTemplateId],
    enabled: !!selectedTemplateId,
    queryFn: async () => {
      const res = await api.get('/category-landing-templates', { params: { category: selected }, __silent: true })
      const list = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const found = (asCollection(list)).find((x) => x.id === selectedTemplateId)
      if (found) return found
      throw new Error('템플릿을 찾을 수 없습니다.')
    },
    refetchOnWindowFocus: false,
  })

  const buildEmptyForm = React.useCallback(() => getEmptyTemplateForm(), [])

  const loadDefaultTemplate = React.useCallback(async (showToast = true, style = 'default') => {
    const codePreset = getCodeTemplatePreset(selected, style)
    if (codePreset) {
      setForm({
        name: codePreset.name || '',
        heroTitle: codePreset.heroTitle || '',
        heroSubtitle: codePreset.heroSubtitle || '',
        heroImageFileId: codePreset.heroImageFileId || '',
        formTitle: codePreset.formTitle || '',
        contentSections: prettyJsonValue(codePreset.contentSections || []),
        formFields: prettyJsonValue(codePreset.formFields || []),
        surveyCustomFields: prettyJsonValue(codePreset.surveyCustomFields || []),
        customConfig: prettyJsonValue(normalizeCategoryLandingLayoutConfig(codePreset.customConfig || {})),
      })
      if (showToast) {
        try {
          window?.toast?.show?.(getBasicTemplateLoadMessage(style, true), 'success')
        } catch { }
      }
      return
    }
    try {
      const endpoint = style === 'clean-form'
        ? `/category-landing-templates/${selected}/clean-form`
        : style === 'hanwha-sections'
          ? `/category-landing-templates/${selected}/hanwha-sections`
          : `/category-landing-templates/${selected}`
      const res = await api.get(endpoint, { __silent: true })
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (data) {
        setForm({
          name: data.name || '',
          heroTitle: data.heroTitle || '',
          heroSubtitle: data.heroSubtitle || '',
          heroImageFileId: data.heroImageFileId || '',
          formTitle: data.formTitle || '',
          contentSections: prettyJsonValue(data.contentSections || '[]'),
          formFields: prettyJsonValue(data.formFields || '[]'),
          surveyCustomFields: prettyJsonValue(data.surveyCustomFields || '[]'),
          customConfig: prettyJsonValue(normalizeCategoryLandingLayoutConfig(data.customConfig || {})),
        })
        if (showToast) {
          try {
            window?.toast?.show?.(getBasicTemplateLoadMessage(style), 'success')
          } catch { }
        }
      }
    } catch (e) {
      if (showToast) {
        try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '템플릿 불러오기 실패', 'error') } catch { }
      }
    }
  }, [selected])

  React.useEffect(() => {
    const firstKey = codeTemplateOptions[0]?.presetKey || 'default'
    setSelectedCodeTemplateKey(firstKey)
  }, [codeTemplateOptions])

  React.useEffect(() => {
    const items = asCollection(listQuery.data)
    if (items.length > 0) {
      setSelectedTemplateId(items[0].id)
    } else {
      setSelectedTemplateId(null)
      // 저장된 템플릿이 없으면 기본 템플릿 자동 로드 (토스트 없이)
      loadDefaultTemplate(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQuery.data, selected])

  const selectedSavedTemplate = React.useMemo(
    () => (asCollection(listQuery.data)).find((item) => item.id === selectedTemplateId) || null,
    [listQuery.data, selectedTemplateId],
  )

  const refetchTemplates = () => {
    listQuery.refetch()
    if (selectedTemplateId) detailQuery.refetch()
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      validateJsonFields()
      const payload = buildTemplatePayload(form, selected)
      const res = await api.post('/category-landing-templates', payload)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: (data) => {
      try { window?.toast?.show?.('템플릿이 생성되었습니다', 'success') } catch { }
      setSelectedTemplateId(data?.id || null)
      refetchTemplates()
    },
    onError: (e) => { try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '생성 실패', 'error') } catch { } },
  })

  React.useEffect(() => {
    const d = detailQuery.data
    if (!d) {
      if (!selectedTemplateId) setForm(buildEmptyForm())
      return
    }
    setForm({
      name: d.name || '',
      heroTitle: d.heroTitle || '',
      heroSubtitle: d.heroSubtitle || '',
      heroImageFileId: d.heroImageFileId || '',
      formTitle: d.formTitle || '',
      contentSections: prettyJsonValue(d.contentSections || '[]'),
      formFields: prettyJsonValue(d.formFields || '[]'),
      surveyCustomFields: prettyJsonValue(d.surveyCustomFields || '[]'),
      customConfig: prettyJsonValue(normalizeCategoryLandingLayoutConfig(d.customConfig || '{}')),
    })
  }, [detailQuery.data, selectedTemplateId, buildEmptyForm])

  const editorState = useCategoryLandingEditorState({
    jsonMode: editorMode === 'json',
    fields: [
      { key: 'contentSections', label: '본문 섹션', kind: 'array', raw: form.contentSections, setRaw: (value) => setForm((prev) => ({ ...prev, contentSections: value })), fallback: [] },
      { key: 'formFields', label: '폼 필드', kind: 'array', raw: form.formFields, setRaw: (value) => setForm((prev) => ({ ...prev, formFields: value })), fallback: createCategoryLandingDefaultFormFields() },
      { key: 'surveyCustomFields', label: '설문 필드', kind: 'array', raw: form.surveyCustomFields, setRaw: (value) => setForm((prev) => ({ ...prev, surveyCustomFields: value })), fallback: [] },
      { key: 'customConfig', label: '추가 설정', kind: 'object', raw: form.customConfig, setRaw: (value) => setForm((prev) => ({ ...prev, customConfig: value })), fallback: {} },
    ],
  })

  const contentSectionsParsed = editorState.parsedFields.contentSections
  const formFieldsParsed = editorState.parsedFields.formFields
  const surveyFieldsParsed = editorState.parsedFields.surveyCustomFields
  const customConfigParsed = editorState.parsedFields.customConfig
  const validateJsonFields = editorState.validateJsonFields

  const changeEditorMode = (mode) => {
    if (mode === 'block') {
      try {
        validateJsonFields()
      } catch (e) {
        try { window?.toast?.show?.(e?.message || 'JSON 형식을 확인해 주세요.', 'error') } catch { }
        return
      }
    }
    setEditorMode(mode)
  }

  const setArrayField = (field, nextValue) => {
    setForm((prev) => ({ ...prev, [field]: prettyJsonValue(nextValue) }))
  }

  const setObjectField = (field, nextValue) => {
    setForm((prev) => ({ ...prev, [field]: prettyJsonValue(nextValue) }))
  }

  const updateCustomConfig = (key, value) => {
    setObjectField('customConfig', { ...customConfigParsed.value, [key]: value })
  }
  const currentLayoutKey = customConfigParsed.value?.templateStyle || 'default'
  const currentLayoutEditableFields = getCategoryLandingLayoutEditableFields(currentLayoutKey)
  const changeLayout = (layoutKey) => {
    setObjectField('customConfig', applyCategoryLandingLayout(layoutKey, customConfigParsed.value || {}))
  }

  const uploadMedia = async (file) => {
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      try { window?.toast?.show?.('파일 크기는 10MB 이하만 가능합니다.', 'error') } catch { }
      return
    }
    setUploadingTarget('heroImage')
    try {
      const data = new FormData()
      data.append('file', file)
      // Content-Type은 지정하지 않음 — axios가 boundary 포함 multipart 헤더를 설정함
      const res = await api.post('/files/upload/public-landing-image', data)
      const fileId = res?.data?.data?.fileId || res?.data?.fileId
      if (!fileId) throw new Error('파일 식별자를 받지 못했습니다.')
      setForm((prev) => ({ ...prev, heroImageFileId: fileId }))
      try { window?.toast?.show?.('이미지가 업로드되었습니다.', 'success') } catch { }
    } catch (e) {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '이미지 업로드 실패', 'error') } catch { }
    } finally {
      setUploadingTarget('')
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplateId) throw new Error('수정할 템플릿을 먼저 선택해 주세요.')
      validateJsonFields()
      await api.put(`/category-landing-templates/id/${selectedTemplateId}`, buildTemplatePayload(form, selected))
    },
    onSuccess: () => {
      try { window?.toast?.show?.('템플릿 저장 완료', 'success') } catch { }
      refetchTemplates()
    },
    onError: (e) => { try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '저장 실패', 'error') } catch { } },
  })

  const deleteMutation = useMutation({
    mutationFn: async (templateId) => {
      await api.delete(`/category-landing-templates/id/${templateId}`)
    },
    onSuccess: () => {
      try { window?.toast?.show?.('템플릿이 삭제되었습니다', 'success') } catch { }
      setSelectedTemplateId(null)
      setForm(buildEmptyForm())
      refetchTemplates()
    },
    onError: (e) => { try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '삭제 실패', 'error') } catch { } },
  })

  if (listQuery.isLoading) return <Loading />
  if (listQuery.isError) return <InlineError message={listQuery.error?.message || '불러오기 실패'} />

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-3xl font-bold">템플릿관리 &gt; 홍보용페이지</h1>
        <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
        <div className="space-y-3 rounded-2xl border p-4">
          {CATEGORIES.map((c) => (
            <button key={c.code} type="button" className={`w-full rounded-xl px-4 py-3 text-left text-base font-medium ${selected === c.code ? 'border border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'hover:bg-slate-50'}`} onClick={() => setSelected(c.code)}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="space-y-5 rounded-2xl border p-5 md:col-span-3">
          <div className="flex items-center justify-between gap-2">
            <label className="text-base font-medium text-slate-600">저장된 템플릿</label>
            <div className="flex items-center gap-2">
              <select
                className="min-w-[200px] rounded-xl border px-4 py-3 text-base"
                value={selectedCodeTemplateKey}
                onChange={(e) => setSelectedCodeTemplateKey(e.target.value || 'default')}
              >
                {codeTemplateOptions.map((option) => (
                  <option key={option.value} value={option.presetKey}>{option.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="rounded-xl border px-4 py-2.5 text-base hover:bg-slate-50"
                onClick={() => loadDefaultTemplate(true, selectedCodeTemplateKey)}
              >
                코드 템플릿 불러오기
              </button>
              <button type="button" className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-base font-semibold text-teal-800 hover:bg-teal-100" onClick={() => { setSelectedTemplateId(null); setForm(buildEmptyForm()) }}>
                새 템플릿
              </button>
            </div>
          </div>
          {selectedCodeTemplateHint && (
            <div className="rounded-xl border bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
              <strong className="text-slate-700">{selectedCodeTemplateHint.title}</strong> {selectedCodeTemplateHint.description}
            </div>
          )}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-700">기본 코드 템플릿 빠른 선택</div>
              <div className="text-xs text-slate-500">카드를 누르면 바로 불러옵니다.</div>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {codeTemplateCards.map((item) => {
                const isActive = selectedCodeTemplateKey === item.presetKey
                return (
                  <button
                    key={item.presetKey}
                    type="button"
                    onClick={() => {
                      setSelectedCodeTemplateKey(item.presetKey)
                      loadDefaultTemplate(true, item.presetKey)
                    }}
                    className={`overflow-hidden rounded-2xl border text-left transition ${isActive ? 'border-brand-sky ring-2 ring-brand-sky/20' : 'hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    <TemplateThumb
                      src={item.preset?.heroImageFileId}
                      alt={item.label}
                      className="h-36 w-full border-b border-slate-100 bg-slate-100"
                    />
                    <div className="space-y-2 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900">{item.label}</div>
                        {isActive ? <span className="rounded-full border border-brand-navy/20 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-brand-navy">선택됨</span> : null}
                      </div>
                      <div className="text-xs font-medium text-slate-500">{item.hintTitle || '기본 템플릿'}</div>
                      <div className="line-clamp-2 text-sm leading-6 text-slate-600">{item.hintDescription || item.preset?.heroSubtitle || '카테고리에 맞는 기본 랜딩 구조입니다.'}</div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
          <select
            className="w-full rounded-xl border px-4 py-3 text-base"
            value={selectedTemplateId || ''}
            onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">신규 템플릿 작성</option>
            {(asCollection(listQuery.data)).map((t) => (
              <option key={t.id} value={t.id}>{t.name || `템플릿 #${t.id}`}</option>
            ))}
          </select>
          {selectedSavedTemplate && (
            <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[220px_minmax(0,1fr)]">
              <TemplateThumb
                src={selectedSavedTemplate.heroImageFileId}
                alt={selectedSavedTemplate.name || '저장된 템플릿'}
                className="h-36 w-full bg-slate-100"
              />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700">저장된 템플릿</span>
                  <span className="text-xs text-slate-500">#{selectedSavedTemplate.id}</span>
                </div>
                <div className="text-lg font-semibold text-slate-900">{selectedSavedTemplate.name || `템플릿 #${selectedSavedTemplate.id}`}</div>
                <div className="line-clamp-2 text-sm text-slate-600">{selectedSavedTemplate.heroTitle || '상단 제목이 아직 없습니다.'}</div>
                <div className="line-clamp-2 text-xs leading-5 text-slate-500">{selectedSavedTemplate.heroSubtitle || '상단 설명이 아직 없습니다.'}</div>
              </div>
            </div>
          )}
          <CategoryLandingEditorModeToggle
            title="편집 방식"
            description="같은 데이터를 블록 편집 또는 JSON 편집으로 수정할 수 있습니다."
            mode={editorMode}
            onChange={changeEditorMode}
            activeClassName="border border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10"
            inactiveClassName="border hover:bg-slate-50"
            options={[
              { value: 'block', label: '블록 편집' },
              { value: 'json', label: 'JSON 편집' },
            ]}
          />

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <div className="space-y-4">
              <div><label className="text-base font-medium text-slate-600">템플릿명</label><input className="mt-2 w-full rounded-xl border px-4 py-3 text-base" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
              <div>
                <label className="text-base font-medium text-slate-600">상단 제목</label>
                <textarea className="mt-2 min-h-[88px] w-full rounded-xl border px-4 py-3 text-base" rows={3} value={form.heroTitle} onChange={(e) => setForm((p) => ({ ...p, heroTitle: e.target.value }))} />
                <div className="mt-1 text-xs text-slate-500">엔터로 줄바꿈하면 공개 페이지에도 반영됩니다.</div>
              </div>
              <div><label className="text-base font-medium text-slate-600">상단 설명</label><textarea className="mt-2 min-h-[110px] w-full rounded-xl border px-4 py-3 text-base" value={form.heroSubtitle} onChange={(e) => setForm((p) => ({ ...p, heroSubtitle: e.target.value }))} /></div>
              <div className="space-y-3 rounded-2xl border p-5">
                <div className="text-base font-semibold">상단 대표 이미지</div>
                <div className="text-xs text-slate-500">권장 가로 1920px 이상(16:9~3:2). 레이아웃에 따라 잘릴 수 있습니다.</div>
                <div className="flex gap-2">
                  <input className="w-full rounded-xl border px-4 py-3 text-base" value={form.heroImageFileId} onChange={(e) => setForm((p) => ({ ...p, heroImageFileId: e.target.value }))} placeholder="파일 ID 또는 이미지 업로드" />
                  <label className="cursor-pointer rounded-xl border bg-blue-50 px-4 py-3 text-base font-semibold hover:bg-slate-50">
                    이미지 업로드
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0])} />
                  </label>
                </div>
                {uploadingTarget && <div className="text-sm text-slate-500">업로드 중...</div>}
                {form.heroImageFileId && (
                  <div className="rounded-xl border bg-slate-50 p-3">
                    <img src={resolveApiAssetUrl(form.heroImageFileId)} alt="" className="max-h-48 rounded mx-auto" />
                  </div>
                )}
              </div>
              <div><label className="text-base font-medium text-slate-600">폼 제목</label><input className="mt-2 w-full rounded-xl border px-4 py-3 text-base" value={form.formTitle} onChange={(e) => setForm((p) => ({ ...p, formTitle: e.target.value }))} /></div>

              {editorMode === 'block' ? (
                <div className="space-y-4">
                  <JsonErrorNotice label="본문 섹션" error={contentSectionsParsed.error} />
                  <JsonErrorNotice label="폼 필드" error={formFieldsParsed.error} />
                  <JsonErrorNotice label="설문 필드" error={surveyFieldsParsed.error} />
                  <JsonErrorNotice label="추가 설정" error={customConfigParsed.error} />

                  <CategoryLandingSectionBlockEditor
                    sections={contentSectionsParsed.value}
                    onChange={(next) => setArrayField('contentSections', next)}
                    title="본문 섹션"
                  />

                  <CategoryLandingFieldListEditor
                    title="상담 폼 필드"
                    items={formFieldsParsed.value}
                    onChange={(next) => setArrayField('formFields', next)}
                    keyName="name"
                    keyLabel="필드 키"
                    addLabel="필드 추가"
                    createItem={() => ({ name: '', label: '', required: false, type: 'text' })}
                  />

                  <CategoryLandingFieldListEditor
                    title="설문 필드"
                    items={surveyFieldsParsed.value}
                    onChange={(next) => setArrayField('surveyCustomFields', next)}
                    keyName="id"
                    keyLabel="질문 ID"
                    addLabel="질문 추가"
                    createItem={getEmptySurveyField}
                  />

                  <div className="space-y-4 rounded-2xl border p-5">
                    <div className="text-lg font-semibold">레이아웃 / 공통 스타일 설정</div>
                    <div className="text-sm leading-6 text-slate-500">레이아웃 구조는 고정되며, 아래 공통 인터페이스만 수정할 수 있습니다.</div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-slate-500">레이아웃 템플릿</label>
                        <select className="mt-2 w-full rounded-xl border px-3 py-3 text-base" value={currentLayoutKey} onChange={(e) => changeLayout(e.target.value)}>
                          {layoutOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {(currentLayoutKey === 'hanwha-sections' || isLeadGenFullbleedLayoutKey(currentLayoutKey)) && (
                          <div className="mt-2 text-sm leading-6 text-slate-500">
                            추천상품은 별도 편집하지 않으며, 공개 설정된 다른 카테고리 랜딩페이지 중에서 자동으로 랜덤 노출됩니다.
                          </div>
                        )}
                      </div>
                      {currentLayoutEditableFields.map((field) => (
                        <div key={field.key}>
                          <label className="text-sm font-medium text-slate-500">{field.label}</label>
                          {field.type === 'number' ? (
                            <input
                              type="number"
                              className="mt-2 w-full rounded-xl border px-3 py-3 text-base"
                              value={customConfigParsed.value?.[field.key] ?? ''}
                              onChange={(e) => updateCustomConfig(field.key, coerceLayoutFieldValue(field, e.target.value))}
                              placeholder={field.placeholder || ''}
                            />
                          ) : isCategoryLandingLayoutColorKey(field.key) ? (
                            <CategoryLandingLayoutColorField
                              wrapperClassName="mt-2"
                              value={customConfigParsed.value?.[field.key] ?? ''}
                              onChange={(v) => updateCustomConfig(field.key, coerceLayoutFieldValue(field, v))}
                              placeholder={field.placeholder || ''}
                            />
                          ) : (
                            <input
                              type="text"
                              className="mt-2 w-full rounded-xl border px-3 py-3 text-base"
                              value={customConfigParsed.value?.[field.key] ?? ''}
                              onChange={(e) => updateCustomConfig(field.key, coerceLayoutFieldValue(field, e.target.value))}
                              placeholder={field.placeholder || ''}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <CategoryLandingJsonEditorCard
                    title="본문 섹션(JSON)"
                    description="JSON 편집은 고급 모드입니다. 저장 전 문법 오류가 없는지 확인해 주세요."
                    value={form.contentSections}
                    onChange={(value) => setForm((p) => ({ ...p, contentSections: value }))}
                    error={contentSectionsParsed.error}
                    minHeight={180}
                  />
                  <CategoryLandingJsonEditorCard
                    title="폼 필드(JSON)"
                    value={form.formFields}
                    onChange={(value) => setForm((p) => ({ ...p, formFields: value }))}
                    error={formFieldsParsed.error}
                    minHeight={160}
                  />
                  <CategoryLandingJsonEditorCard
                    title="설문 필드(JSON)"
                    value={form.surveyCustomFields}
                    onChange={(value) => setForm((p) => ({ ...p, surveyCustomFields: value }))}
                    error={surveyFieldsParsed.error}
                    minHeight={160}
                  />
                  <CategoryLandingJsonEditorCard
                    title="추가 설정(JSON 읽기전용)"
                    value={prettyJsonValue(normalizeCategoryLandingLayoutConfig(customConfigParsed.value || {}))}
                    onChange={() => {}}
                    error=""
                    minHeight={120}
                    readOnly
                  />
                </div>
              )}
            </div>

            <div className="space-y-3">
              <CategoryLandingLivePreview
                heroTitle={form.heroTitle}
                heroSubtitle={form.heroSubtitle}
                heroImageFileId={form.heroImageFileId}
                sections={contentSectionsParsed.value}
                formTitle={form.formTitle}
                formFields={formFieldsParsed.value}
                customConfig={customConfigParsed.value}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!selectedTemplateId ? (
              <button type="button" className="rounded-xl bg-brand-navy px-5 py-3 text-base font-semibold text-white disabled:opacity-60" disabled={createMutation.isLoading} onClick={() => createMutation.mutate()}>
                {createMutation.isLoading ? '생성 중...' : '템플릿 생성'}
              </button>
            ) : (
              <>
                <button type="button" className="rounded-xl bg-brand-navy px-5 py-3 text-base font-semibold text-white disabled:opacity-60" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
                  {saveMutation.isLoading ? '저장 중...' : '템플릿 저장'}
                </button>
                <button 
                  type="button" 
                  className="rounded-xl border border-red-300 px-5 py-3 text-base font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60" 
                  disabled={deleteMutation.isLoading}
                  onClick={() => {
                    if (window.confirm('정말 이 템플릿을 삭제하시겠습니까?')) {
                      deleteMutation.mutate(selectedTemplateId)
                    }
                  }}
                >
                  {deleteMutation.isLoading ? '삭제 중...' : '템플릿 삭제'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
